import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, X } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import type { DateRange } from "react-day-picker";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { DialogBody } from '@/components/ui/dialog-extensions';
import { useIsMobile } from '@/hooks/useMobile';
import { AddressAutocomplete } from '@/components/ui/address-autocomplete';

interface TeamMember {
  user_id: string;
  email: string;
  profiles?: {
    first_name: string;
    last_name: string;
  };
  role?: string;
  status: string;
}

interface CreateSiteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function CreateSiteDialog({ open, onOpenChange, onSuccess }: CreateSiteDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  const [formData, setFormData] = useState({
    name: '',
    location: '',
    dateRange: undefined as DateRange | undefined,
    selectedSiteManagers: [] as string[],
    selectedProjectSupervisors: [] as string[]
  });

  useEffect(() => {
    if (open) {
      loadTeamMembers();
    }
  }, [user, open]);

  const loadTeamMembers = async () => {
    if (!user?.company_id) {
      setAuthLoading(false);
      return;
    }

    try {
      // Fetch all profiles from company (include current user for role selection)
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, email, first_name, last_name')
        .eq('company_id', user.company_id);

      if (profilesError) {
        toast.error("Impossible de charger les profils de l'équipe");
        setAuthLoading(false);
        return;
      }

      // Fetch all roles
      const { data: rolesData, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id, role')
        .in('user_id', profilesData?.map(p => p.user_id) || []);

      if (rolesError) {
        toast.error("Impossible de charger les rôles");
      }

      // Enrich members with role data AND filter out company_director
      const enrichedMembers: TeamMember[] = profilesData
        .map(profile => ({
          user_id: profile.user_id,
          email: profile.email,
          profiles: {
            first_name: profile.first_name,
            last_name: profile.last_name
          },
          role: rolesData?.find(r => r.user_id === profile.user_id)?.role || 'site_manager',
          status: 'active'
        }))
        .filter(member => member.role !== 'company_director');

      setTeamMembers(enrichedMembers);
      setAuthLoading(false);
    } catch (error) {
      toast.error("Impossible de charger les membres de l'équipe");
      setAuthLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.dateRange?.from || !formData.dateRange?.to) {
      toast.error("Dates manquantes", { description: "Veuillez sélectionner les dates de début et de fin" });
      return;
    }

    if (!user?.id || !user?.company_id) {
      toast.error("Erreur", { description: "Utilisateur non authentifié" });
      return;
    }

    // Only company_director and project_supervisor can create sites
    if (user.role !== 'company_director' && user.role !== 'project_supervisor') {
      toast.error("Action non autorisée", { description: "Seuls les directeurs et conducteurs peuvent créer des chantiers" });
      return;
    }

    setIsLoading(true);

    try {
      // site_manager_id = first selected site_manager, or null if none selected
      const primarySiteManagerId = formData.selectedSiteManagers.length > 0 
        ? formData.selectedSiteManagers[0] 
        : null;

      const { data: newSite, error: siteError } = await supabase
        .from('sites')
        .insert({
          name: formData.name,
          location: formData.location,
          start_date: formData.dateRange.from.toISOString().split('T')[0],
          end_date: formData.dateRange.to.toISOString().split('T')[0],
          company_id: user.company_id,
          site_manager_id: primarySiteManagerId,
          status: 'planning'
        })
        .select()
        .single();

      if (siteError) throw siteError;

      // Auto-assign the creator if they're a project_supervisor
      if (newSite && user.role === 'project_supervisor') {
        const { error: selfAssignError } = await supabase
          .from('site_assignments')
          .insert({
            site_id: newSite.id,
            user_id: user.id,
            role: 'project_supervisor' as const,
            assigned_by: user.id
          });

        if (selfAssignError) {
          toast.error("Erreur lors de l'auto-assignation au chantier");
        }
      }

      // Assign selected site managers
      if (formData.selectedSiteManagers.length > 0 && newSite) {
        const smAssignments = formData.selectedSiteManagers.map(userId => ({
          site_id: newSite.id,
          user_id: userId,
          role: 'site_manager' as const,
          assigned_by: user.id
        }));

        const { error: smError } = await supabase
          .from('site_assignments')
          .insert(smAssignments);

        if (smError) {
          toast.error("Erreur lors de l'assignation des chefs de chantier");
        }
      }

      // Assign selected project supervisors (exclude creator if already auto-assigned)
      const supervisorsToAssign = formData.selectedProjectSupervisors.filter(
        userId => !(user.role === 'project_supervisor' && userId === user.id)
      );
      
      if (supervisorsToAssign.length > 0 && newSite) {
        const psAssignments = supervisorsToAssign.map(userId => ({
          site_id: newSite.id,
          user_id: userId,
          role: 'project_supervisor' as const,
          assigned_by: user.id
        }));

        const { error: psError } = await supabase
          .from('site_assignments')
          .insert(psAssignments);

        if (psError) {
          toast.error("Erreur lors de l'assignation des conducteurs de travaux");
        }
      }

      // Invalidate authorized site IDs cache so the new site is immediately accessible
      await queryClient.invalidateQueries({ queryKey: ['authorized-site-ids'] });

      toast.success("Chantier créé", { description: "Le nouveau chantier a été créé avec succès" });

      onSuccess();
      onOpenChange(false);
      
      // Reset form
      setFormData({
        name: '',
        location: '',
        dateRange: undefined,
        selectedSiteManagers: [],
        selectedProjectSupervisors: []
      });
    } catch (error: any) {
      toast.error("Erreur", { description: error.message || "Une erreur est survenue lors de la création du chantier" });
    } finally {
      setIsLoading(false);
    }
  };

  const getAvailableSiteManagers = () => {
    return teamMembers.filter(m => m.role === 'site_manager' && !formData.selectedSiteManagers.includes(m.user_id));
  };

  const getAvailableProjectSupervisors = () => {
    return teamMembers.filter(m => m.role === 'project_supervisor' && !formData.selectedProjectSupervisors.includes(m.user_id));
  };

  const getSelectedSiteManagers = () => {
    return teamMembers.filter(m => formData.selectedSiteManagers.includes(m.user_id));
  };

  const getSelectedProjectSupervisors = () => {
    return teamMembers.filter(m => formData.selectedProjectSupervisors.includes(m.user_id));
  };

  const addSiteManager = (userId: string) => {
    if (userId && !formData.selectedSiteManagers.includes(userId)) {
      setFormData(prev => ({ ...prev, selectedSiteManagers: [...prev.selectedSiteManagers, userId] }));
    }
  };

  const removeSiteManager = (userId: string) => {
    setFormData(prev => ({ ...prev, selectedSiteManagers: prev.selectedSiteManagers.filter(id => id !== userId) }));
  };

  const addProjectSupervisor = (userId: string) => {
    if (userId && !formData.selectedProjectSupervisors.includes(userId)) {
      setFormData(prev => ({ ...prev, selectedProjectSupervisors: [...prev.selectedProjectSupervisors, userId] }));
    }
  };

  const removeProjectSupervisor = (userId: string) => {
    setFormData(prev => ({ ...prev, selectedProjectSupervisors: prev.selectedProjectSupervisors.filter(id => id !== userId) }));
  };

  if (authLoading) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "flex flex-col overflow-hidden max-h-[85vh] sm:max-w-3xl"
        )}
      >
        <DialogHeader>
          <DialogTitle>Créer un nouveau chantier</DialogTitle>
        </DialogHeader>
        <DialogBody>
      <form id="create-site-form" onSubmit={handleSubmit} className="space-y-xl px-1">
            <Card>
              <CardHeader>
                <CardTitle>Informations générales</CardTitle>
              </CardHeader>
              <CardContent className="space-y-md">
                <div className="space-y-sm">
                  <Label htmlFor="name">Nom du chantier *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Chantier A"
                    required
                  />
                </div>
                <div className="space-y-sm">
                  <Label htmlFor="location">Localisation *</Label>
                  <AddressAutocomplete
                    id="location"
                    value={formData.location}
                    onChange={(value) => setFormData({ ...formData, location: value })}
                    placeholder="Ex: 123 Rue de la Paix, Paris"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Planning</CardTitle>
              </CardHeader>
              <CardContent className="space-y-md">
                <div className="space-y-sm">
                  <Label>Période du chantier *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left",
                          !formData.dateRange && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-sm h-4 w-4" />
                        {formData.dateRange?.from ? (
                          formData.dateRange.to ? (
                            <>
                              {format(formData.dateRange.from, "dd MMM yyyy", { locale: fr })} -{" "}
                              {format(formData.dateRange.to, "dd MMM yyyy", { locale: fr })}
                            </>
                          ) : (
                            format(formData.dateRange.from, "dd MMM yyyy", { locale: fr })
                          )
                        ) : (
                          <span>Sélectionner les dates</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="range"
                        defaultMonth={formData.dateRange?.from}
                        selected={formData.dateRange}
                        onSelect={(range) => setFormData(prev => ({ ...prev, dateRange: range }))}
                        numberOfMonths={2}
                        className="rounded-lg border border-border pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Équipe assignée</CardTitle>
              </CardHeader>
              <CardContent className="space-y-md">
                {/* Chefs de chantier dropdown */}
                <div className="space-y-sm">
                  <Label>Chef(s) de chantier</Label>
                  <Select onValueChange={addSiteManager}>
                    <SelectTrigger>
                      <SelectValue placeholder="Ajouter un chef de chantier..." />
                    </SelectTrigger>
                    <SelectContent className="bg-background">
                      <SelectGroup>
                        <SelectLabel>Chef de chantier</SelectLabel>
                        {getAvailableSiteManagers().map(member => (
                          <SelectItem key={member.user_id} value={member.user_id}>
                            {member.profiles?.first_name} {member.profiles?.last_name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  {getSelectedSiteManagers().length > 0 && (
                    <div className="flex flex-wrap gap-sm pt-xs">
                      {getSelectedSiteManagers().map(member => (
                        <Badge key={member.user_id} variant="secondary" className="px-md py-1.5">
                          <span className="mr-sm">
                            {member.profiles?.first_name} {member.profiles?.last_name}
                          </span>
                          <X
                            className="h-3 w-3 cursor-pointer hover:text-destructive"
                            onClick={() => removeSiteManager(member.user_id)}
                          />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Conducteurs de travaux dropdown */}
                <div className="space-y-sm">
                  <Label>Conducteur(s) de travaux</Label>
                  <Select onValueChange={addProjectSupervisor}>
                    <SelectTrigger>
                      <SelectValue placeholder="Ajouter un conducteur de travaux..." />
                    </SelectTrigger>
                    <SelectContent className="bg-background">
                      <SelectGroup>
                        <SelectLabel>Conducteur de travaux</SelectLabel>
                        {getAvailableProjectSupervisors().map(member => (
                          <SelectItem key={member.user_id} value={member.user_id}>
                            {member.profiles?.first_name} {member.profiles?.last_name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  {getSelectedProjectSupervisors().length > 0 && (
                    <div className="flex flex-wrap gap-sm pt-xs">
                      {getSelectedProjectSupervisors().map(member => (
                        <Badge key={member.user_id} variant="secondary" className="px-md py-1.5">
                          <span className="mr-sm">
                            {member.profiles?.first_name} {member.profiles?.last_name}
                          </span>
                          <X
                            className="h-3 w-3 cursor-pointer hover:text-destructive"
                            onClick={() => removeProjectSupervisor(member.user_id)}
                          />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

          </form>
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button type="submit" form="create-site-form" disabled={isLoading}>
            {isLoading ? "Création..." : "Créer le chantier"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
